import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import "../today-rooms.css";
import type { createAgentClient } from "../api/agentClient";
import type { BoardQueueRow, ClinicRoom, StaffRequest } from "../api/clinicalWorkspace";
import { boardStatusLabels, canStartVisit, isScheduled, orderBoardRows } from "../lib/todayBoard";
import { DiscardConfirmDialog } from "../pages/IceBlueConfirmDiscard";
import { Icon } from "./Icon";
import { PhysicianRosterBadge } from "./PhysicianRosterBadge";
import { StaffRequestComposer, type StaffRequestDraft } from "./StaffRequestComposer";
import { TodayRoomPicker } from "./TodayRoomPicker";
import { TodayVisitCloser, type VisitCloseOutcome } from "./TodayVisitCloser";

type RoomsClient = Pick<
  ReturnType<typeof createAgentClient>,
  | "createClinicRoom"
  | "updateClinicRoom"
  | "deleteClinicRoom"
  | "assignAppointmentRoom"
  | "releaseAppointmentRoom"
>;
export type RoomVisit = Pick<
  BoardQueueRow,
  | "id"
  | "encounterId"
  | "appointmentId"
  | "appointmentVersion"
  | "appointmentStatus"
  | "displayName"
  | "encounterSigned"
  | "room"
>;
type RoomPatient = Pick<BoardQueueRow, "appointmentId" | "appointmentVersion" | "displayName">;
type PhysicianOption = { userId: string; displayName: string; active: boolean };
export type RoomsBoardPreferences = {
  scope: "mine" | "all";
  zoneFilter: string;
  layout: "tiles" | "list";
  lobbyCollapsed?: boolean;
};
type ComposerPatient = { patientId: string; encounterId: string; displayName: string };
type RoomChanges = { name?: string; zone?: string; zonePhysicianUserId?: string | null };

// Ownership is explicit metadata. A patient's treating/roster physician never defines a zone.
const zoneKey = (room: ClinicRoom) =>
  JSON.stringify([room.zone, room.zonePhysician?.userId ?? null]);
function roomDetails(room: ClinicRoom, rows: BoardQueueRow[]) {
  const row = room.appointmentId
    ? rows.find((candidate) => candidate.appointmentId === room.appointmentId)
    : undefined;
  const displayName = room.displayName ?? row?.displayName ?? "Patient";
  const version = room.appointmentVersion ?? row?.appointmentVersion;
  const occupant: RoomPatient | undefined =
    room.state === "occupied" && room.appointmentId && version !== undefined
      ? { appointmentId: room.appointmentId, appointmentVersion: version, displayName }
      : undefined;
  const encounterId = room.encounterId ?? row?.encounterId;
  const patientId = room.patientId ?? row?.id;
  const appointmentStatus = room.appointmentStatus ?? row?.appointmentStatus;
  const visit: RoomVisit | undefined =
    occupant && encounterId && patientId
      ? {
          ...occupant,
          id: patientId,
          encounterId,
          appointmentStatus,
          encounterSigned: room.encounterSigned ?? row?.encounterSigned,
          room: room.name,
        }
      : undefined;
  const stage =
    appointmentStatus === "in_progress"
      ? "with-clinician"
      : appointmentStatus === "ready_for_discharge"
        ? "wrap-up"
        : appointmentStatus === "roomed"
          ? "ready"
          : row?.status;
  const status =
    room.state === "available"
      ? "Available"
      : room.state === "turnover"
        ? "Needs cleaning"
        : stage
          ? boardStatusLabels[stage]
          : "Occupied";
  return {
    row,
    occupant,
    encounterId,
    patientId,
    visit,
    stage,
    status,
    displayName,
    rosterPhysician: room.rosterPhysician ?? row?.rosterPhysician,
  };
}

/** Renames a room and edits its zone; only the fields that changed are sent. */
function RoomEditor({
  room,
  rooms,
  physicians,
  directoryUnavailable,
  disabled,
  onSave,
}: {
  room: ClinicRoom;
  rooms: ClinicRoom[];
  physicians: PhysicianOption[];
  directoryUnavailable: boolean;
  disabled: boolean;
  onSave: (changes: RoomChanges) => Promise<boolean>;
}) {
  const [name, setName] = useState(room.name);
  const [zone, setZone] = useState(room.zone);
  const [owner, setOwner] = useState(room.zonePhysician?.userId ?? "");
  const nameErrorId = useId();
  const savedOwner = room.zonePhysician?.userId ?? "";
  const options =
    room.zonePhysician && !physicians.some((person) => person.userId === room.zonePhysician?.userId)
      ? [room.zonePhysician, ...physicians]
      : physicians;
  const newName = name.trim();
  const newZone = zone.trim() || "Shared";
  const changes: RoomChanges = {
    ...(newName !== room.name ? { name: newName } : {}),
    ...(newZone !== room.zone ? { zone: newZone } : {}),
    ...(owner !== savedOwner ? { zonePhysicianUserId: owner || null } : {}),
  };
  // The database is the final check; these catch the common mistakes before a round trip.
  const nameProblem =
    changes.name === undefined
      ? undefined
      : !newName
        ? "Enter a room name."
        : ["lobby", "unassigned"].includes(newName.toLocaleLowerCase())
          ? "Choose a name other than Lobby or Unassigned."
          : rooms.some(
                (other) =>
                  other.id !== room.id &&
                  other.name.toLocaleLowerCase() === newName.toLocaleLowerCase(),
              )
            ? `Another room is already called ${newName}.`
            : undefined;
  const blocked =
    disabled ||
    Object.keys(changes).length === 0 ||
    nameProblem !== undefined ||
    (directoryUnavailable && changes.zonePhysicianUserId !== undefined);
  return (
    <form
      className="tb-room-editor"
      aria-label={`Edit ${room.name}`}
      onSubmit={(event) => {
        event.preventDefault();
        if (!blocked) void onSave(changes);
      }}
    >
      <label>
        Room name
        <input
          value={name}
          maxLength={40}
          disabled={disabled}
          aria-invalid={nameProblem !== undefined}
          aria-describedby={nameProblem ? nameErrorId : undefined}
          onChange={(event) => setName(event.target.value)}
        />
      </label>
      {nameProblem ? (
        <p id={nameErrorId} className="tb-room-editor-error">
          {nameProblem}
        </p>
      ) : null}
      <label>
        Zone
        <input
          value={zone}
          maxLength={80}
          disabled={disabled}
          onChange={(event) => setZone(event.target.value)}
        />
      </label>
      <label>
        Zone physician
        <select
          value={owner}
          disabled={disabled || directoryUnavailable}
          onChange={(event) => setOwner(event.target.value)}
        >
          <option value="">Shared / no owner</option>
          {options.map((person) => (
            <option key={person.userId} value={person.userId} disabled={!person.active}>
              {person.displayName}
              {!person.active ? " (inactive)" : ""}
            </option>
          ))}
        </select>
      </label>
      <button type="submit" className="tb-button" disabled={blocked}>
        Save room
      </button>
    </form>
  );
}

export function TodayRoomsBoard({
  siteId,
  rooms,
  rows,
  selectedId,
  search = "",
  client,
  disabled,
  onChanged,
  onSelect,
  onOpen,
  onStart,
  onWrap,
  onComplete,
  onClose,
  staffRequests,
  onCreateStaffRequest,
  actorUserId,
  physicians = [],
  physiciansLoading = false,
  physiciansError,
  onRetryPhysicians,
  preferences,
  onPreferencesChange,
  onClearSearch,
  roomRevealRequest,
  staffPanel,
  staffPanelOpen = false,
  onCloseStaffPanel,
  lobbyRowExtra,
}: {
  siteId: string;
  rooms: ClinicRoom[];
  rows: BoardQueueRow[];
  selectedId: string | undefined;
  search?: string;
  client: RoomsClient;
  disabled: boolean;
  onChanged: () => Promise<void>;
  onSelect: (id: string) => void;
  onOpen: (row: { id: string; encounterId: string; displayName?: string }) => void;
  onStart: (row: BoardQueueRow) => void;
  onWrap: (row: BoardQueueRow) => void;
  onComplete: (row: RoomVisit) => void;
  /** The patient left or the visit is cancelled; the room goes to cleaning. */
  onClose: (row: RoomVisit, outcome: VisitCloseOutcome) => void;
  staffRequests: StaffRequest[];
  onCreateStaffRequest: (
    patient: { patientId: string; encounterId: string },
    draft: StaffRequestDraft,
  ) => Promise<void>;
  actorUserId?: string;
  physicians?: PhysicianOption[];
  physiciansLoading?: boolean;
  physiciansError?: string;
  onRetryPhysicians?: () => void;
  preferences?: RoomsBoardPreferences;
  onPreferencesChange?: (preferences: RoomsBoardPreferences) => void;
  onClearSearch?: () => void;
  roomRevealRequest?: { roomId: string; requestId: number } | undefined;
  staffPanel?: ReactNode;
  staffPanelOpen?: boolean;
  onCloseStaffPanel?: (restoreFocus?: boolean) => void;
  /** Extra state shown on a Lobby patient, such as their questionnaire's review pill. */
  lobbyRowExtra?: (row: BoardQueueRow) => ReactNode;
}) {
  const [localPreferences, setLocalPreferences] = useState<RoomsBoardPreferences>(() => ({
    scope: rooms.some((room) => actorUserId && room.zonePhysician?.userId === actorUserId)
      ? "mine"
      : "all",
    zoneFilter: "",
    layout: "tiles",
  }));
  const view = preferences ?? localPreferences;
  const lobbyCollapsed = view.lobbyCollapsed ?? false;
  const selectionRevision = useRef(0);
  const appliedRoomReveal = useRef<typeof roomRevealRequest>(undefined);
  const latestSearch = useRef(search);
  useEffect(() => {
    if (latestSearch.current !== search) {
      latestSearch.current = search;
      selectionRevision.current += 1;
    }
  }, [search]);
  const changeView = (next: Partial<RoomsBoardPreferences>) => {
    if (next.scope !== undefined || next.zoneFilter !== undefined) selectionRevision.current += 1;
    const value = { ...view, ...next };
    setLocalPreferences(value);
    onPreferencesChange?.(value);
  };
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [assigningId, setAssigningId] = useState<string | null>(null);
  const [patientToAssign, setPatientToAssign] = useState("");
  const [requestingEncounter, setRequestingEncounter] = useState<string | null>(null);
  // These stay mounted through deselection, filtering, movement and a completed visit.
  const [composers, setComposers] = useState<ComposerPatient[]>([]);
  const [editing, setEditing] = useState(false);
  // The room as it was when Delete was pressed; its version guards the deletion.
  const [confirmingDelete, setConfirmingDelete] = useState<ClinicRoom | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [zone, setZone] = useState("Shared");
  const [zoneOwner, setZoneOwner] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [hoveredRoomId, setHoveredRoomId] = useState<string | null>(null);
  const mutationLock = useRef(false);
  const creationKey = useRef(crypto.randomUUID());
  const panel = useRef<HTMLElement>(null);
  const roomButtons = useRef(new Map<string, HTMLButtonElement>());
  const staffButton = useRef<HTMLButtonElement>(null);
  const editToggle = useRef<HTMLButtonElement>(null);
  const scrollPanel = useRef(false);
  const filterId = useId();
  const lobbyId = useId();
  const deleteHelpId = useId();
  const busy = disabled || pending;
  const query = search.trim().toLocaleLowerCase();
  const matches = (row: BoardQueueRow) =>
    [row.displayName, row.reason, row.room, row.clinician, row.rosterPhysician?.displayName].some(
      (value) => value?.toLocaleLowerCase().includes(query),
    );
  const movable = orderBoardRows(
    rows.filter(
      (row) =>
        ["waiting", "roomed", "in_progress", "ready_for_discharge"].includes(
          row.appointmentStatus ?? "",
        ) &&
        row.appointmentId !== undefined &&
        row.appointmentVersion !== undefined,
    ),
  );
  const lobby = orderBoardRows(
    rows.filter(
      (row) =>
        !isScheduled(row) &&
        matches(row) &&
        !rooms.some(
          (room) => room.state === "occupied" && room.appointmentId === row.appointmentId,
        ),
    ),
  );
  const assignableLobby = movable.filter(
    (row) =>
      !rooms.some((room) => room.state === "occupied" && room.appointmentId === row.appointmentId),
  );
  const draggedPatient = assignableLobby.find((row) => row.encounterId === draggingId);
  const assigning = assignableLobby.find((row) => row.encounterId === assigningId);
  const selectedRoom = rooms.find((room) => room.id === selectedRoomId);
  const selected = selectedRoom ? roomDetails(selectedRoom, rows) : undefined;
  const zones = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; name: string; owner?: PhysicianOption | undefined; rooms: ClinicRoom[] }
    >();
    for (const room of rooms) {
      const key = zoneKey(room);
      const group = groups.get(key) ?? {
        key,
        name: room.zone,
        owner: room.zonePhysician,
        rooms: [] as ClinicRoom[],
      };
      group.rooms.push(room);
      groups.set(key, group);
    }
    return [...groups.values()]
      .sort(
        (a, b) =>
          a.name.localeCompare(b.name) ||
          (a.owner?.displayName ?? "").localeCompare(b.owner?.displayName ?? ""),
      )
      .map((group) => ({
        ...group,
        rooms: group.rooms.sort((a, b) =>
          a.name.localeCompare(b.name, undefined, { numeric: true }),
        ),
      }));
  }, [rooms]);
  const visibleZones = zones
    .filter(
      (group) =>
        (view.scope === "all" || (actorUserId && group.owner?.userId === actorUserId)) &&
        (!view.zoneFilter || group.key === view.zoneFilter),
    )
    .map((group) => ({
      ...group,
      rooms: group.rooms.filter((room) => {
        const details = roomDetails(room, rows);
        return (
          !query ||
          [
            room.name,
            room.zone,
            details.displayName,
            details.row?.reason,
            details.rosterPhysician?.displayName,
          ].some((value) => value?.toLocaleLowerCase().includes(query))
        );
      }),
    }))
    .filter((group) => group.rooms.length);
  const visibleRoomIds = visibleZones.flatMap((group) => group.rooms.map((room) => room.id));
  const visibleKey = visibleRoomIds.join("|");
  useEffect(() => {
    if (!roomRevealRequest || appliedRoomReveal.current === roomRevealRequest) return;
    appliedRoomReveal.current = roomRevealRequest;
    if (!rooms.some((room) => room.id === roomRevealRequest.roomId)) return;
    selectionRevision.current += 1;
    scrollPanel.current = true;
    setSelectedRoomId(roomRevealRequest.roomId);
    setAssigningId(null);
    setRequestingEncounter(null);
    setPatientToAssign("");
  }, [roomRevealRequest, rooms]);
  useEffect(() => {
    if (selectedRoomId && !visibleKey.split("|").includes(selectedRoomId)) {
      setSelectedRoomId(null);
      setRequestingEncounter(null);
    }
  }, [selectedRoomId, visibleKey]);
  useEffect(() => {
    if (patientToAssign && !assignableLobby.some((row) => row.encounterId === patientToAssign))
      setPatientToAssign("");
  }, [patientToAssign, assignableLobby]);
  useEffect(() => {
    if (assigningId && !assignableLobby.some((row) => row.encounterId === assigningId))
      setAssigningId(null);
  }, [assigningId, assignableLobby]);
  useEffect(() => {
    if (draggingId && (busy || !draggedPatient)) setDraggingId(null);
    if (
      hoveredRoomId &&
      (busy ||
        !draggedPatient ||
        !visibleKey.split("|").includes(hoveredRoomId) ||
        !rooms.some((room) => room.id === hoveredRoomId && room.state === "available"))
    )
      setHoveredRoomId(null);
  }, [draggingId, draggedPatient, busy, hoveredRoomId, visibleKey, rooms]);
  useEffect(() => {
    if ((scrollPanel.current && (selectedRoomId || assigningId)) || staffPanelOpen) {
      scrollPanel.current = false;
      const top = panel.current?.getBoundingClientRect().top;
      if (top !== undefined && (top < 0 || top > window.innerHeight - 160))
        panel.current?.scrollIntoView?.({ block: "start", behavior: "auto" });
    }
  }, [selectedRoomId, assigningId, staffPanelOpen]);
  const deselect = () => {
    selectionRevision.current += 1;
    const previous = selectedRoomId;
    setSelectedRoomId(null);
    setAssigningId(null);
    setRequestingEncounter(null);
    if (previous) roomButtons.current.get(previous)?.focus();
  };
  const selectRoom = (room: ClinicRoom) => {
    if (staffPanelOpen) {
      onCloseStaffPanel?.();
      if (selectedRoomId === room.id) return;
    }
    if (selectedRoomId === room.id) {
      deselect();
      return;
    }
    selectionRevision.current += 1;
    scrollPanel.current = true;
    setSelectedRoomId(room.id);
    setAssigningId(null);
    setRequestingEncounter(null);
    setPatientToAssign(
      assignableLobby.some((row) => row.encounterId === selectedId) ? (selectedId ?? "") : "",
    );
  };
  const revealRoom = (room: ClinicRoom) => {
    onCloseStaffPanel?.();
    onClearSearch?.();
    changeView({ scope: "all", zoneFilter: "" });
    scrollPanel.current = true;
    setSelectedRoomId(room.id);
    setAssigningId(null);
    setRequestingEncounter(null);
  };
  const closeComposer = (encounterId: string) => {
    setRequestingEncounter((current) => (current === encounterId ? null : current));
    if (selected?.encounterId === encounterId) staffButton.current?.focus();
  };
  const perform = async (action: () => Promise<unknown>, message: string): Promise<boolean> => {
    if (mutationLock.current || disabled) return false;
    mutationLock.current = true;
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      await action();
      await onChanged();
      setNotice(message);
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The room could not be updated.");
      try {
        await onChanged();
      } catch {
        /* Keep the original actionable error. */
      }
      return false;
    } finally {
      mutationLock.current = false;
      setPending(false);
    }
  };
  const assign = async (room: ClinicRoom, row: RoomPatient | undefined): Promise<boolean> => {
    if (
      !row ||
      room.state !== "available" ||
      !row.appointmentId ||
      row.appointmentVersion === undefined
    )
      return false;
    const revision = selectionRevision.current;
    const saved = await perform(
      () =>
        client.assignAppointmentRoom(row.appointmentId as string, {
          siteId,
          expectedVersion: row.appointmentVersion as number,
          roomId: room.id,
          expectedRoomVersion: room.version,
        }),
      `${row.displayName} is assigned to ${room.name}.`,
    );
    if (saved && revision === selectionRevision.current) revealRoom(room);
    return saved;
  };
  const release = (room: ClinicRoom, row: RoomPatient) => {
    if (room.state !== "occupied" || !row.appointmentId || row.appointmentVersion === undefined)
      return;
    void perform(
      () =>
        client.releaseAppointmentRoom(row.appointmentId as string, {
          siteId,
          expectedVersion: row.appointmentVersion as number,
          roomId: room.id,
          expectedRoomVersion: room.version,
        }),
      `${row.displayName} is back in the lobby; their encounter is still open. ${room.name} needs cleaning.`,
    );
  };
  const createRoom = async () => {
    if (!name.trim() || (zoneOwner && (physiciansLoading || physiciansError))) return;
    const saved = await perform(
      () =>
        client.createClinicRoom({
          siteId,
          name: name.trim(),
          zone: zone.trim() || "Shared",
          ...(zoneOwner ? { zonePhysicianUserId: zoneOwner } : {}),
          idempotencyKey: creationKey.current,
        }),
      `${name.trim()} is ready to use.`,
    );
    if (saved) {
      creationKey.current = crypto.randomUUID();
      setName("");
      setZone("Shared");
      setZoneOwner("");
      setAdding(false);
    }
  };
  const deleteRoom = async (room: ClinicRoom) => {
    setConfirmingDelete(null);
    const deleted = await perform(
      () => client.deleteClinicRoom(room.id, { siteId, expectedVersion: room.version }),
      `${room.name} deleted.`,
    );
    if (!deleted) return;
    selectionRevision.current += 1;
    setSelectedRoomId((current) => (current === room.id ? null : current));
    editToggle.current?.focus();
  };
  // Needs staff is for in-clinic tasks. A referral package hand-off belongs to
  // its holder's Attention list instead (REF-10), so it never shows here.
  const activeStaff = staffRequests.filter(
    (request) =>
      (request.status === "open" || request.status === "in_progress") &&
      request.referralId === undefined,
  );
  return (
    <fieldset
      className="tb-rooms-board"
      aria-label="Rooms board"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || event.defaultPrevented) return;
        event.preventDefault();
        setDraggingId(null);
        setHoveredRoomId(null);
        if (staffPanelOpen) onCloseStaffPanel?.(true);
        else if (requestingEncounter) closeComposer(requestingEncounter);
        else deselect();
      }}
    >
      {error ? (
        <p className="tb-notice tb-notice-error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="sr-only" role="status">
          {notice}
        </p>
      ) : null}
      <div className="tb-rooms-toolbar">
        <div className="tb-room-filters">
          <fieldset className="tb-room-segment" aria-label="Zone scope">
            <button
              type="button"
              aria-pressed={view.scope === "mine"}
              onClick={() => changeView({ scope: "mine", zoneFilter: "" })}
            >
              My zones
            </button>
            <button
              type="button"
              aria-pressed={view.scope === "all"}
              onClick={() => changeView({ scope: "all", zoneFilter: "" })}
            >
              All zones
            </button>
          </fieldset>
          <label className="tb-zone-filter" htmlFor={filterId}>
            <span className="sr-only">Filter rooms by zone</span>
            <select
              id={filterId}
              value={view.zoneFilter}
              onChange={(event) => {
                const group = zones.find((item) => item.key === event.target.value);
                changeView({
                  zoneFilter: event.target.value,
                  ...(group && group.owner?.userId !== actorUserId ? { scope: "all" } : {}),
                });
              }}
            >
              <option value="">
                {view.scope === "mine" ? "All my zones" : "All clinic zones"}
              </option>
              {zones.map((group) => (
                <option key={group.key} value={group.key}>
                  {group.name}
                  {group.owner ? ` · ${group.owner.displayName}` : " · Shared"}
                </option>
              ))}
            </select>
          </label>
          <fieldset className="tb-room-segment" aria-label="Room layout">
            <button
              type="button"
              aria-pressed={view.layout === "tiles"}
              onClick={() => changeView({ layout: "tiles" })}
            >
              Tiles
            </button>
            <button
              type="button"
              aria-pressed={view.layout === "list"}
              onClick={() => changeView({ layout: "list" })}
            >
              List
            </button>
          </fieldset>
        </div>
        <div className="tb-room-management">
          <button
            type="button"
            className="tb-room-link"
            ref={editToggle}
            aria-pressed={editing}
            onClick={() => setEditing(!editing)}
          >
            <Icon name="pencil" />
            {editing ? "Done editing" : "Edit rooms"}
          </button>
          <button
            type="button"
            className="tb-room-link"
            aria-expanded={adding}
            onClick={() => setAdding(!adding)}
          >
            <Icon name="plus" />
            Add room
          </button>
        </div>
      </div>
      {(adding || editing) && physiciansError ? (
        <p className="tb-notice tb-notice-error" role="alert">
          {physiciansError} Zone physician choices are unavailable.{" "}
          {onRetryPhysicians ? (
            <button type="button" className="tb-button" onClick={onRetryPhysicians}>
              Retry physicians
            </button>
          ) : null}
        </p>
      ) : null}
      {(adding || editing) && physiciansLoading ? (
        <p className="tb-muted">Loading zone physicians…</p>
      ) : null}
      {adding ? (
        <form
          className="tb-add-room"
          onSubmit={(event) => {
            event.preventDefault();
            void createRoom();
          }}
        >
          <label>
            Room name
            <input
              required
              maxLength={40}
              value={name}
              disabled={busy}
              onChange={(event) => setName(event.target.value)}
              placeholder="Room 1"
            />
          </label>
          <label>
            Clinician zone
            <input
              maxLength={80}
              value={zone}
              disabled={busy}
              onChange={(event) => setZone(event.target.value)}
            />
          </label>
          <label>
            Zone physician
            <select
              value={zoneOwner}
              disabled={busy || physiciansLoading || !!physiciansError}
              onChange={(event) => setZoneOwner(event.target.value)}
            >
              <option value="">Shared / no owner</option>
              {physicians
                .filter((person) => person.active)
                .map((person) => (
                  <option key={person.userId} value={person.userId}>
                    {person.displayName}
                  </option>
                ))}
            </select>
          </label>
          <button type="submit" className="tb-button tb-primary" disabled={busy}>
            {pending ? "Saving…" : "Create room"}
          </button>
        </form>
      ) : null}
      {editing ? (
        <p className="tb-muted">
          Select a room to rename it, change its zone, or delete it. A zone never changes the
          patient’s clinician.
        </p>
      ) : null}
      <div
        className="tb-room-workspace"
        data-layout={view.layout}
        data-lobby-collapsed={lobbyCollapsed}
      >
        <aside className="tb-lobby-column" aria-label="Lobby" data-collapsed={lobbyCollapsed}>
          <header className="tb-lobby-header">
            <h2 hidden={lobbyCollapsed}>
              <Icon name="inbox" /> Lobby <span>{lobby.length}</span>
            </h2>
            <button
              type="button"
              className="tb-lobby-toggle"
              aria-label={lobbyCollapsed ? "Expand lobby" : "Collapse lobby"}
              aria-expanded={!lobbyCollapsed}
              aria-controls={lobbyId}
              onClick={() => changeView({ lobbyCollapsed: !lobbyCollapsed })}
            >
              <Icon name="chevron" />
              {lobbyCollapsed ? (
                <>
                  <span className="tb-lobby-collapsed-label">Lobby</span>
                  <span>{lobby.length}</span>
                </>
              ) : null}
            </button>
          </header>
          <div id={lobbyId} hidden={lobbyCollapsed}>
            <p className="tb-lobby-guidance">Drag a patient to a room or choose Assign.</p>
            {lobby.length ? (
              <ul>
                {lobby.map((row) => (
                  <li key={row.encounterId}>
                    <div>
                      <button
                        type="button"
                        className="tb-lobby-patient"
                        aria-pressed={assigningId === row.encounterId}
                        draggable={!busy && movable.includes(row)}
                        aria-label={`Select ${row.displayName} for a room`}
                        onClick={() => {
                          onCloseStaffPanel?.();
                          selectionRevision.current += 1;
                          onSelect(row.encounterId);
                          setAssigningId(row.encounterId);
                          setSelectedRoomId(null);
                          setRequestingEncounter(null);
                          scrollPanel.current = true;
                        }}
                        onDragStart={(event) => {
                          if (busy || !movable.includes(row)) {
                            event.preventDefault();
                            return;
                          }
                          setHoveredRoomId(null);
                          setDraggingId(row.encounterId);
                          onSelect(row.encounterId);
                          event.dataTransfer.setData("text/plain", row.encounterId);
                          event.dataTransfer.effectAllowed = "move";
                        }}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setHoveredRoomId(null);
                        }}
                      >
                        {row.displayName}
                      </button>
                      <small>
                        {row.waitMinutes}m · {boardStatusLabels[row.status]}
                      </small>
                      <PhysicianRosterBadge physician={row.rosterPhysician} />
                      {lobbyRowExtra?.(row)}
                    </div>
                    <button
                      type="button"
                      className="tb-room-link"
                      disabled={busy || !movable.includes(row)}
                      aria-label={`Assign room for ${row.displayName}`}
                      onClick={() => {
                        onCloseStaffPanel?.();
                        selectionRevision.current += 1;
                        onSelect(row.encounterId);
                        setAssigningId(row.encounterId);
                        setSelectedRoomId(null);
                        setRequestingEncounter(null);
                        scrollPanel.current = true;
                      }}
                    >
                      Assign <Icon name="chevron" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No patients in the lobby.</p>
            )}
          </div>
        </aside>
        <div className="tb-zone-board">
          <p className="tb-room-summary">
            {visibleRoomIds.length} rooms ·{" "}
            {
              visibleZones
                .flatMap((group) => group.rooms)
                .filter((room) => room.state === "occupied").length
            }{" "}
            occupied ·{" "}
            {
              visibleZones
                .flatMap((group) => group.rooms)
                .filter((room) => room.state === "available").length
            }{" "}
            available
          </p>
          {visibleZones.map((group) => (
            <section
              className="tb-zone"
              key={group.key}
              aria-label={`${group.name}${group.owner ? ` · ${group.owner.displayName}` : ""} rooms`}
            >
              <header>
                <h3>{group.name}</h3>
                <span>{group.owner?.displayName ?? "Shared"}</span>
                <small>{group.rooms.length} rooms</small>
              </header>
              <div className="tb-room-grid">
                {group.rooms.map((room) => {
                  const details = roomDetails(room, rows);
                  const requests = activeStaff.filter(
                    (request) => request.encounterId === details.encounterId,
                  );
                  const tone =
                    room.state !== "occupied"
                      ? room.state
                      : requests.length
                        ? "needs-staff"
                        : (details.stage ?? "ready");
                  return (
                    <section
                      className={`tb-room-tile tb-room-tone-${tone}${hoveredRoomId === room.id && draggedPatient && !busy && room.state === "available" ? " is-drop-target" : ""}`}
                      key={room.id}
                      aria-label={room.name}
                      onDragEnter={(event) => {
                        if (!busy && draggedPatient && room.state === "available") {
                          event.preventDefault();
                          setHoveredRoomId(room.id);
                        }
                      }}
                      onDragOver={(event) => {
                        if (!busy && draggedPatient && room.state === "available") {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                          setHoveredRoomId(room.id);
                        }
                      }}
                      onDragLeave={(event) => {
                        const next =
                          event.relatedTarget ??
                          document.elementFromPoint?.(event.clientX, event.clientY);
                        if (next instanceof Node && event.currentTarget.contains(next)) return;
                        setHoveredRoomId((current) => (current === room.id ? null : current));
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        setDraggingId(null);
                        setHoveredRoomId(null);
                        if (!busy && room.state === "available" && draggedPatient)
                          void assign(room, draggedPatient);
                      }}
                    >
                      <button
                        type="button"
                        className="tb-room-select"
                        ref={(element) => {
                          if (element) roomButtons.current.set(room.id, element);
                          else roomButtons.current.delete(room.id);
                        }}
                        aria-pressed={selectedRoomId === room.id}
                        aria-label={`Select ${room.name}${room.state === "occupied" ? `, ${details.displayName}` : ""}, ${details.status}`}
                        onClick={() => selectRoom(room)}
                      >
                        <span className="tb-room-tile-top">
                          <span>{room.name}</span>
                          {requests.length ? (
                            <span
                              className="tb-room-request-indicator"
                              role="img"
                              aria-label={`${requests.length} staff requests`}
                            >
                              <Icon name="bell" />
                              <span>{requests.length}</span>
                            </span>
                          ) : room.state === "turnover" ? (
                            <Icon name="sparkles" />
                          ) : null}
                        </span>
                        <span className="tb-room-tile-person">
                          <strong>
                            {room.state === "occupied" ? details.displayName : details.status}
                          </strong>
                          {room.state === "occupied" ? (
                            <PhysicianRosterBadge physician={details.rosterPhysician} />
                          ) : null}
                        </span>
                        <span className="tb-room-tile-bottom">
                          <span>{details.status}</span>
                          <small>
                            {room.state === "available"
                              ? "Ready for a patient"
                              : room.state === "turnover"
                                ? "Prepare for the next patient"
                                : details.visit?.encounterSigned
                                  ? "Note signed"
                                  : details.row
                                    ? `${details.row.waitMinutes}m since arrival`
                                    : "Patient in room"}
                          </small>
                        </span>
                      </button>
                    </section>
                  );
                })}
              </div>
            </section>
          ))}
          {!visibleZones.length ? (
            <div className="tb-room-empty">
              <h3>{rooms.length ? "No rooms in this view" : "Set up your rooms"}</h3>
              <p>
                {rooms.length
                  ? view.scope === "mine"
                    ? "No rooms match your assigned zones and current filters."
                    : "Try another zone or search."
                  : "Add rooms to organize your clinic."}
              </p>
              {rooms.length ? (
                <button
                  type="button"
                  className="tb-button"
                  onClick={() => changeView({ scope: "all", zoneFilter: "" })}
                >
                  Show all zones
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <aside
          className="tb-room-action-column"
          ref={panel}
          aria-label={staffPanelOpen ? "Needs staff" : "Room actions"}
        >
          <div id="tb-staff-panel" className="tb-staff-column" hidden={!staffPanelOpen}>
            <header className="tb-staff-column-heading">
              <h2>Needs staff</h2>
              <button
                type="button"
                className="tb-room-close"
                aria-label="Close needs staff"
                onClick={() => onCloseStaffPanel?.(true)}
              >
                <Icon name="close" />
              </button>
            </header>
            {staffPanel}
          </div>
          <div
            className="tb-room-action-panel"
            hidden={staffPanelOpen || (!selectedRoom && !assigning)}
          >
            <header>
              <span>
                {selectedRoom ? `${selectedRoom.name} · ${selectedRoom.zone}` : "From the lobby"}
              </span>
              <button
                type="button"
                className="tb-room-close"
                aria-label="Deselect room"
                onClick={deselect}
              >
                <Icon name="close" />
              </button>
            </header>
            {assigning ? (
              <>
                <h2>{assigning.displayName}</h2>
                <p className="tb-room-panel-reason">{assigning.reason}</p>
                <PhysicianRosterBadge physician={assigning.rosterPhysician} />
                <p className="tb-muted">
                  Choose any available room. The patient’s clinician stays the same.
                </p>
                <TodayRoomPicker
                  key={assigning.encounterId}
                  patientName={assigning.displayName}
                  rooms={rooms}
                  disabled={busy}
                  onAssign={(room) => assign(room, assigning)}
                />
              </>
            ) : selectedRoom && selected ? (
              <>
                <h2>
                  {selectedRoom.state === "occupied" ? selected.displayName : selected.status}
                </h2>
                <p className="tb-room-panel-reason">
                  {selectedRoom.state === "occupied"
                    ? (selected.row?.reason ?? "Patient in room")
                    : selectedRoom.state === "available"
                      ? "Choose a patient from the lobby."
                      : "Clean and prepare this room before the next patient."}
                </p>
                {selectedRoom.state === "occupied" ? (
                  <>
                    <PhysicianRosterBadge physician={selected.rosterPhysician} />
                    <p className="tb-room-panel-status">
                      {selected.status}
                      {selected.visit?.encounterSigned ? " · Note signed" : ""}
                      {selectedRoom.clinician ? ` · ${selectedRoom.clinician}` : ""}
                    </p>
                    <div className="tb-room-panel-actions">
                      {selected.row ? (
                        <button
                          type="button"
                          className="tb-button tb-primary"
                          disabled={busy}
                          aria-label={`${canStartVisit(selected.row) ? "Start encounter" : "Encounter"} for ${selected.displayName}`}
                          onClick={() => {
                            if (!selected.row) return;
                            if (canStartVisit(selected.row)) onStart(selected.row);
                            else onOpen(selected.row);
                          }}
                        >
                          <Icon name="document" />
                          {canStartVisit(selected.row)
                            ? "Start encounter"
                            : selected.stage === "with-clinician"
                              ? "Resume encounter"
                              : "Encounter"}
                        </button>
                      ) : selected.patientId && selected.encounterId ? (
                        <button
                          type="button"
                          className="tb-button tb-primary"
                          aria-label={`Encounter for ${selected.displayName}`}
                          onClick={() =>
                            onOpen({
                              id: selected.patientId as string,
                              encounterId: selected.encounterId as string,
                              displayName: selected.displayName,
                            })
                          }
                        >
                          <Icon name="document" />
                          Encounter
                        </button>
                      ) : null}
                      {selected.visit ? (
                        <button
                          type="button"
                          className="tb-button"
                          disabled={busy}
                          ref={staffButton}
                          aria-expanded={requestingEncounter === selected.encounterId}
                          onClick={() => {
                            const visit = selected.visit;
                            if (!visit) return;
                            setComposers((current) =>
                              current.some((item) => item.encounterId === visit.encounterId)
                                ? current
                                : [
                                    ...current,
                                    {
                                      patientId: visit.id,
                                      encounterId: visit.encounterId,
                                      displayName: visit.displayName,
                                    },
                                  ],
                            );
                            setRequestingEncounter((current) =>
                              current === visit.encounterId ? null : visit.encounterId,
                            );
                          }}
                        >
                          <Icon name="bell" />
                          Needs staff
                        </button>
                      ) : null}
                      {selected.row && selected.row.appointmentStatus === "in_progress" ? (
                        <button
                          type="button"
                          className="tb-button"
                          disabled={busy}
                          onClick={() => selected.row && onWrap(selected.row)}
                        >
                          Wrap up
                        </button>
                      ) : null}
                      {selected.occupant ? (
                        <>
                          <TodayRoomPicker
                            key={selectedRoom.id + selected.encounterId}
                            patientName={selected.displayName}
                            rooms={rooms}
                            label="Move room"
                            disabled={busy}
                            onAssign={(destination) => assign(destination, selected.occupant)}
                          />
                          <button
                            type="button"
                            className="tb-button"
                            disabled={busy}
                            onClick={() =>
                              selected.occupant && release(selectedRoom, selected.occupant)
                            }
                            title="Keep the visit open in the lobby; this room will need cleaning"
                          >
                            Return to lobby
                          </button>
                        </>
                      ) : null}
                      {selected.visit ? (
                        <button
                          type="button"
                          className="tb-button tb-room-done"
                          disabled={busy || !selected.visit.encounterSigned}
                          onClick={() => selected.visit && onComplete(selected.visit)}
                          title="A signed encounter note is required; signing also marks the visit done automatically"
                        >
                          <Icon name="check" />
                          Mark done
                        </button>
                      ) : null}
                      {selected.visit ? (
                        <TodayVisitCloser
                          key={selectedRoom.id + selected.encounterId}
                          row={selected.visit}
                          disabled={busy}
                          onClose={(outcome) => selected.visit && onClose(selected.visit, outcome)}
                        />
                      ) : null}
                    </div>
                    <p className="tb-room-helper">
                      {selected.visit?.encounterSigned
                        ? "Mark done finishes the visit and sends this room to cleaning."
                        : "Signing the note finishes the visit and sends this room to cleaning."}
                    </p>
                    {activeStaff.some((request) => request.encounterId === selected.encounterId) ? (
                      <p className="tb-room-staff-status">
                        <Icon name="bell" />
                        Needs staff ·{" "}
                        {
                          activeStaff.filter(
                            (request) => request.encounterId === selected.encounterId,
                          ).length
                        }{" "}
                        request(s)
                      </p>
                    ) : null}
                  </>
                ) : selectedRoom.state === "turnover" ? (
                  <button
                    type="button"
                    className="tb-button tb-primary"
                    disabled={busy}
                    aria-label={`Mark ${selectedRoom.name} clean`}
                    onClick={() =>
                      void perform(
                        () =>
                          client.updateClinicRoom(selectedRoom.id, {
                            siteId,
                            expectedVersion: selectedRoom.version,
                            targetState: "available",
                          }),
                        `${selectedRoom.name} is clean and available.`,
                      )
                    }
                  >
                    <Icon name="sparkles" />
                    Mark clean
                  </button>
                ) : (
                  <form
                    className="tb-room-assign-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void assign(
                        selectedRoom,
                        assignableLobby.find((row) => row.encounterId === patientToAssign),
                      );
                    }}
                  >
                    <label>
                      Patient to room
                      <select
                        value={patientToAssign}
                        disabled={busy}
                        onChange={(event) => {
                          setPatientToAssign(event.target.value);
                          onSelect(event.target.value);
                        }}
                      >
                        <option value="">Select a patient</option>
                        {assignableLobby.map((row) => (
                          <option key={row.encounterId} value={row.encounterId}>
                            {row.displayName}
                          </option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="submit"
                      className="tb-button tb-primary"
                      disabled={busy || !patientToAssign}
                      aria-label={`Assign ${assignableLobby.find((row) => row.encounterId === patientToAssign)?.displayName ?? "patient"} to ${selectedRoom.name}`}
                    >
                      Assign patient
                    </button>
                  </form>
                )}
                {editing ? (
                  <>
                    <RoomEditor
                      key={`${selectedRoom.id}-${selectedRoom.version}`}
                      room={selectedRoom}
                      rooms={rooms}
                      physicians={physicians}
                      directoryUnavailable={physiciansLoading || !!physiciansError}
                      disabled={busy}
                      onSave={async (changes) => {
                        const revision = selectionRevision.current;
                        const saved = await perform(
                          () =>
                            client.updateClinicRoom(selectedRoom.id, {
                              siteId,
                              expectedVersion: selectedRoom.version,
                              ...changes,
                            }),
                          changes.name === undefined
                            ? `${selectedRoom.name} zone updated.`
                            : `${selectedRoom.name} is now ${changes.name}.`,
                        );
                        if (saved && revision === selectionRevision.current)
                          revealRoom(selectedRoom);
                        return saved;
                      }}
                    />
                    {selectedRoom.state === "available" ? (
                      <button
                        type="button"
                        className="tb-button"
                        disabled={busy}
                        onClick={() =>
                          void perform(
                            () =>
                              client.updateClinicRoom(selectedRoom.id, {
                                siteId,
                                expectedVersion: selectedRoom.version,
                                targetState: "turnover",
                              }),
                            `${selectedRoom.name} needs cleaning.`,
                          )
                        }
                      >
                        Mark for turnover
                      </button>
                    ) : null}
                    <div className="tb-room-delete">
                      <button
                        type="button"
                        className="tb-button"
                        disabled={busy || selectedRoom.state === "occupied"}
                        aria-describedby={
                          selectedRoom.state === "occupied" ? deleteHelpId : undefined
                        }
                        onClick={() => setConfirmingDelete(selectedRoom)}
                      >
                        <Icon name="trash" />
                        Delete room
                      </button>
                      {selectedRoom.state === "occupied" ? (
                        <p id={deleteHelpId} className="tb-room-helper">
                          Move the patient out of {selectedRoom.name} before deleting it.
                        </p>
                      ) : null}
                    </div>
                  </>
                ) : null}
              </>
            ) : null}
            {composers.map((patient) => (
              <StaffRequestComposer
                key={patient.encounterId}
                active={
                  requestingEncounter === patient.encounterId &&
                  selected?.encounterId === patient.encounterId
                }
                patientName={patient.displayName}
                location={
                  rooms.find(
                    (room) => room.encounterId === patient.encounterId && room.state === "occupied",
                  )?.name
                }
                disabled={busy}
                onCancel={() => closeComposer(patient.encounterId)}
                onSubmit={async (draft) => {
                  await onCreateStaffRequest(
                    { patientId: patient.patientId, encounterId: patient.encounterId },
                    draft,
                  );
                  setRequestingEncounter((current) =>
                    current === patient.encounterId ? null : current,
                  );
                  setNotice(`Staff request sent for ${patient.displayName}.`);
                }}
              />
            ))}
          </div>
        </aside>
      </div>
      <details className="tb-room-workflow">
        <summary>How room actions work</summary>
        <p>
          <strong>Sign the encounter note:</strong> the visit is marked done automatically and the
          room needs cleaning. <strong>Mark done</strong> finishes a visit whose note is already
          signed.
        </p>
        <p>
          <strong>Return to lobby:</strong> free the room while keeping the encounter open.{" "}
          <strong>Move room:</strong> continue the same encounter elsewhere. Both leave the previous
          room needing cleaning.
        </p>
        <p>
          <strong>Wrap up:</strong> the patient stays in the room while the visit is being finished.
          Unfinished staff requests stay in Needs staff after a visit is done.
        </p>
        <p>
          <strong>Cancel visit:</strong> a patient who left, or a visit opened by mistake, is
          recorded as left or cancelled and removed from Today. The room needs cleaning, and an
          unsigned note stays as a draft that can no longer be signed.
        </p>
      </details>
      {confirmingDelete ? (
        <DiscardConfirmDialog
          request={{
            title: `Delete ${confirmingDelete.name}?`,
            detail: (
              <>
                <p>
                  {confirmingDelete.name} comes off the Rooms board for everyone at this clinic.
                  This cannot be undone.
                </p>
                <p>
                  Past visits keep the room name in their records, and you can add a room with this
                  name again later.
                </p>
              </>
            ),
            secondaryLabel: "Cancel",
            confirmLabel: "Delete room",
            tone: "danger",
            onConfirm: () => void deleteRoom(confirmingDelete),
          }}
          onCancel={() => setConfirmingDelete(null)}
          onSecondary={() => setConfirmingDelete(null)}
          onConfirm={() => void deleteRoom(confirmingDelete)}
        />
      ) : null}
    </fieldset>
  );
}
